import { useState, useEffect, useCallback } from 'react'
import { preseaApi } from '../../api/presea'
import Boton from '../../components/Boton'
import { estadoPillStyle, fmtFecha, st } from './_common'

/**
 * Conexión + cola de envíos a Presea (lo que se le manda y la respuesta).
 * Hereda de la PreseaPage original (24/06/26: dividido en 3 sub-páginas).
 */
export default function PreseaCola() {
  const [estado, setEstado] = useState(null)
  const [probando, setProbando] = useState(false)
  const [procesando, setProcesando] = useState(false)
  const [limpiando, setLimpiando] = useState(false)
  const [salidaProceso, setSalidaProceso] = useState('')
  const [error, setError] = useState('')

  const [cola, setCola] = useState([])
  const [meta, setMeta] = useState(null)
  const [resumen, setResumen] = useState({})
  const [filtros, setFiltros] = useState({ estado: '', metodo: '', q: '' })
  const [page, setPage] = useState(1)
  const [loadingCola, setLoadingCola] = useState(false)

  const [detalle, setDetalle] = useState(null)

  const probar = useCallback(async () => {
    setProbando(true); setError('')
    try {
      const { data } = await preseaApi.test()
      setEstado(data)
    } catch (e) {
      setEstado(null)
      setError(e.response?.data?.message || 'No se pudo contactar al servidor.')
    } finally { setProbando(false) }
  }, [])

  const cargarCola = useCallback(async () => {
    setLoadingCola(true)
    try {
      const params = { page }
      if (filtros.estado) params.estado = filtros.estado
      if (filtros.metodo) params.metodo = filtros.metodo
      if (filtros.q) params.q = filtros.q
      const { data } = await preseaApi.cola(params)
      setCola(data.data || [])
      setMeta(data.meta || null)
      setResumen(data.resumen || {})
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar la cola.')
    } finally { setLoadingCola(false) }
  }, [filtros, page])

  useEffect(() => { probar() }, [probar])
  useEffect(() => { cargarCola() }, [cargarCola])

  const procesarCola = async () => {
    setProcesando(true); setSalidaProceso(''); setError('')
    try {
      const { data } = await preseaApi.procesarCola()
      setSalidaProceso(data?.salida || '(sin salida)')
      cargarCola()
    } catch (e) {
      setError(e.response?.data?.message || 'Error al procesar la cola.')
    } finally { setProcesando(false) }
  }

  // Botón de emergencia (Martin, 10 sep 2026): deja la cola en cero para
  // destrabar la operatoria. Todo lo no-procesado pasa a rechazado, las NVs
  // cuya factura estaba en la cola vuelven a borrador y se vacían los jobs
  // apilados en Redis. Lo procesado no se toca.
  const limpiarCola = async () => {
    if (!window.confirm('Esto marca TODO lo pendiente / error / revisar como RECHAZADO, devuelve a borrador las NVs cuya factura estaba en la cola (habrá que reenviarlas) y vacía los jobs pendientes. Lo ya procesado no se toca.\n\n¿Limpiar toda la cola?')) return
    setLimpiando(true); setSalidaProceso(''); setError('')
    try {
      const { data } = await preseaApi.colaLimpiar()
      setSalidaProceso(`Cola limpiada: ${data.limpiados} ítem(s) pasados a rechazado · ${data.nvs_a_borrador} NV(s) devueltas a borrador · ${data.colas_redis_vaciadas} cola(s) de jobs vaciadas.`)
      cargarCola()
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'No se pudo limpiar la cola.')
    } finally { setLimpiando(false) }
  }

  const reintentar = async (id) => {
    if (!window.confirm('¿Reintentar este envío? Resetea los intentos y lo re-despacha (mismo unique_id — no duplica en Presea).')) return
    setError('')
    try {
      await preseaApi.colaReintentar(id)
      cargarCola()
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'No se pudo reintentar.')
    }
  }

  const verDetalle = async (id) => {
    try {
      const { data } = await preseaApi.colaItem(id)
      setDetalle(data)
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo obtener el detalle.')
    }
  }

  const ok = estado?.ok
  const fake = estado?.respuesta?.fake

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={st.title}>ERP Presea — Cola</h2>
      <p style={st.sub}>
        Lo que se le manda a Presea (NVs, cobros, despachos) y la respuesta cruda. Si una NV o un cobro
        está pendiente, se procesa solo cada minuto; podés forzarlo desde acá.
      </p>

      {/* Conexión */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>Conexión</h3>
          <Boton variante="send" sm onClick={probar} disabled={probando}>
            {probando ? 'Probando…' : '↻ Probar conexión'}
          </Boton>
        </div>
        {probando && !estado ? (
          <p style={st.muted}>Verificando…</p>
        ) : ok ? (
          <div style={{ ...st.estadoBox, ...st.estadoOk }}>
            <span style={st.estadoIcon}>✓</span>
            <div>
              <div style={st.estadoTexto}>Conectado a Presea</div>
              {fake && <div style={st.muted}>Modo simulación activo (PRESEA_FAKE=true): respuestas falsas.</div>}
            </div>
          </div>
        ) : (
          <div style={{ ...st.estadoBox, ...st.estadoFail }}>
            <span style={st.estadoIcon}>✕</span>
            <div>
              <div style={st.estadoTexto}>El botón "Probar conexión" reporta sin conexión</div>
              <div style={st.muted}>{estado?.error || error || 'Revisá la configuración.'}</div>
              <div style={st.hint}>
                <strong>OJO:</strong> este test puede fallar y aun así el chain de Presea (NV → Factura
                → Remito → Recibo) puede estar funcionando — mirá la cola abajo: si los últimos ítems
                aparecen como <code style={st.code}>procesado</code>, las llamadas reales sí están andando.
                Si dice "conexión rechazada": Neuralsoft tiene que habilitar la IP del servidor LGR en su WS.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cola */}
      <div style={st.card}>
        <div style={st.cardHead}>
          <h3 style={st.cardTitle}>Cola de envíos a Presea</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <Boton variante="send" onClick={procesarCola} disabled={procesando || limpiando}>
              {procesando ? 'Procesando…' : '▶ Procesar cola ahora'}
            </Boton>
            <Boton
              variante="destructive" onClick={limpiarCola} disabled={limpiando || procesando}
              title="Emergencia: pasa todo lo pendiente/error/revisar a rechazado, devuelve las NVs a borrador y vacía los jobs pendientes. Lo procesado no se toca."
            >
              {limpiando ? 'Limpiando…' : '🧹 Limpiar toda la cola'}
            </Boton>
          </div>
        </div>

        <div style={st.resumenRow}>
          {Object.entries(resumen).map(([e, c]) => (
            <span key={e} style={{ ...st.pill, ...estadoPillStyle(e) }}>{c} {e}</span>
          ))}
          {Object.keys(resumen).length === 0 && <span style={st.muted}>Cola vacía.</span>}
        </div>

        <div style={st.filtros}>
          <select value={filtros.estado} onChange={e => { setFiltros(f => ({ ...f, estado: e.target.value })); setPage(1) }} style={st.input}>
            <option value="">Todos los estados</option>
            <option value="pendiente">pendiente</option>
            <option value="pendiente_envio">pendiente_envio</option>
            <option value="procesado">procesado</option>
            <option value="error">error</option>
            <option value="revisar">revisar</option>
            <option value="rechazado">rechazado</option>
          </select>
          <input
            type="text" placeholder="Método (ej. generarnotadeventa)"
            value={filtros.metodo}
            onChange={e => { setFiltros(f => ({ ...f, metodo: e.target.value })); setPage(1) }}
            style={st.input}
          />
          <input
            type="text" placeholder="Buscar (unique_id / payload)"
            value={filtros.q}
            onChange={e => { setFiltros(f => ({ ...f, q: e.target.value })); setPage(1) }}
            style={{ ...st.input, flex: 1 }}
          />
        </div>

        {loadingCola ? (
          <p style={st.muted}>Cargando…</p>
        ) : cola.length === 0 ? (
          <p style={st.muted}>No hay ítems con esos filtros.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={st.tabla}>
              <thead>
                <tr>
                  {['ID', 'Unique ID', 'Método', 'NV', 'Estado', 'Intentos', 'Creado', 'Último intento', ''].map(h => (
                    <th key={h} style={st.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cola.map(it => (
                  <tr key={it.id} style={st.tr}>
                    <td style={st.td}>{it.id}</td>
                    <td style={st.td}><code style={st.code}>{it.unique_id}</code></td>
                    <td style={st.td}><code style={st.code}>{it.metodo}</code></td>
                    <td style={st.td}>{it.nota_venta?.numero_lgr || (it.nota_venta_id ? `#${it.nota_venta_id}` : '—')}</td>
                    <td style={st.td}><span style={{ ...st.pillSm, ...estadoPillStyle(it.estado) }}>{it.estado}</span></td>
                    <td style={{ ...st.td, textAlign: 'center' }}>{it.intentos}</td>
                    <td style={st.td}>{fmtFecha(it.created_at)}</td>
                    <td style={st.td}>{fmtFecha(it.ultimo_intento)}</td>
                    <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                      {['error', 'rechazado'].includes(it.estado) && !(it.estado === 'rechazado' && it.metodo === 'generarnotadeventa') && (
                        <Boton
                          variante="send" sm
                          title="Resetea intentos y re-despacha este mismo ítem (mismo unique_id — Presea no duplica)."
                          onClick={() => reintentar(it.id)}
                        >↻ Reintentar</Boton>
                      )}{' '}
                      <Boton variante="secondary" sm onClick={() => verDetalle(it.id)}>Ver JSON →</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div style={st.paginador}>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={st.btnSec}>← Anterior</button>
            <span style={st.muted}>Página {meta.current_page} de {meta.last_page} ({meta.total} ítems)</span>
            <button disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} style={st.btnSec}>Siguiente →</button>
          </div>
        )}

        {salidaProceso && (
          <div style={st.salidaProceso}>
            <strong>Salida del comando:</strong>
            <pre style={st.jsonBox}>{salidaProceso}</pre>
          </div>
        )}
      </div>

      {error && <p style={st.errorMsg}>{error}</p>}

      {detalle && (
        <div style={st.modalOverlay}>
          <div style={st.modalBox} onClick={e => e.stopPropagation()}>
            <div style={st.modalHead}>
              <div>
                <h3 style={{ margin: 0 }}>Ítem #{detalle.id} — <code style={st.code}>{detalle.metodo}</code></h3>
                <div style={st.muted}>unique_id: <code style={st.code}>{detalle.unique_id}</code> · estado: <span style={{ ...st.pillSm, ...estadoPillStyle(detalle.estado) }}>{detalle.estado}</span></div>
              </div>
              <Boton variante="ghost" sm onClick={() => setDetalle(null)}>✕ Cerrar</Boton>
            </div>
            <div style={st.modalSection}>
              <h4 style={st.modalSubtitle}>📤 Payload enviado a Presea</h4>
              <pre style={st.jsonBox}>{JSON.stringify(detalle.payload, null, 2)}</pre>
            </div>
            <div style={st.modalSection}>
              <h4 style={st.modalSubtitle}>📥 Respuesta de Presea</h4>
              {detalle.respuesta ? (
                <pre style={st.jsonBox}>{JSON.stringify(detalle.respuesta, null, 2)}</pre>
              ) : (
                <p style={st.muted}>Sin respuesta todavía (no se procesó).</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
